import { isPlainClick } from '../app/StateLink.jsx';
import { useStoreInfo } from '../app/StoreContext.jsx';
import { count, formatCount } from '../format/count.js';
import { formatPriceRange } from '../format/money.js';
import { formatAverage, ratingText } from '../format/rating.js';
import type { CardFragment, SearchQuery } from '../generated/operations.js';
import { Swatch } from '../search/Swatch.jsx';
import { Stars } from './Stars.jsx';

export type Hit = SearchQuery['search']['items'][number];

export interface ProductCardProps {
    readonly product: CardFragment;
    /** Where the card leads, so it is a real link that opens in a new tab too. */
    readonly href: string;
    readonly onOpen: () => void;
    /** Why the product ranked where it did, shown in explain mode. */
    readonly explain?: ExplainProps | undefined;
}

interface ExplainProps {
    readonly rank: number;
    readonly hit: Hit;
    /** Whether a phrase was typed, so "did not match it" means something. */
    readonly phrase: boolean;
}

type Match = NonNullable<Hit['explanation']>['matches'][number];
type Effect = NonNullable<Hit['explanation']>['rules'][number];

const ROUTES: Partial<Record<Match['route'], (match: Match) => string>> & {
    readonly EXACT: (match: Match) => string;
} = {
    EXACT: (m) => `"${m.typed}" matched "${m.matched}" in the ${m.field}`,
    PREFIX: (m) => `"${m.typed}" is the start of "${m.matched}" in the ${m.field}`,
    SYNONYM: (m) => `"${m.typed}" is a synonym of "${m.matched}" in the ${m.field}`,
    CORRECTED: (m) => `"${m.typed}" is one typo away from "${m.matched}" in the ${m.field}`,
};

/** A route the page does not know yet still reads as a match. */
const matchText = (match: Match): string => (ROUTES[match.route] ?? ROUTES.EXACT)(match);

type Popularity = NonNullable<NonNullable<Hit['explanation']>['popularity']>;

/** What the opens did, in the shopper's words: in how many visits, and by how much. */
const popularityText = ({ clicks, weight }: Popularity, locale: string): string =>
    `Opened from the results in ${count(clicks, 'earlier visit', 'earlier visits', locale)}, score raised by ${Math.round((weight - 1) * 100)}%`;

const effectText = (effect: Effect): string => {
    const weight = effect.weight === null ? '' : ` ${effect.weight}×`;
    if (effect.action === 'PIN') return `Pinned to the top by rule ${effect.ruleId}`;
    if (effect.action === 'BOOST') return `Boosted${weight} by rule ${effect.ruleId}`;
    return `Buried${weight} by rule ${effect.ruleId}`;
};

/**
 * Why one result ranked where it did, in plain words, in the order the
 * question is asked: where it landed, what a rule did to it, what the
 * clicks collected so far did, then what it matched. Rendered only once
 * the answer carrying the explanation has landed, so turning explain mode
 * on never shows a bare rank.
 */
const Explanation = ({ rank, hit, phrase }: ExplainProps) => {
    const { locale } = useStoreInfo();
    if (!hit.explanation) return null;
    const { matches, rules, popularity } = hit.explanation;
    return (
        <ul className="card-explain" aria-label={`Why this is result ${rank}`}>
            <li>
                Result {rank}
                {hit.score !== undefined && `, score ${hit.score.toFixed(2)}`}
            </li>
            {hit.product.membersOnly && <li>Shown to members only</li>}
            {rules.map((effect, index) => (
                <li
                    key={`${effect.ruleId}-${effect.action}-${index}`}
                    className="card-explain-rule"
                >
                    {effectText(effect)}
                </li>
            ))}
            {popularity && <li>{popularityText(popularity, locale)}</li>}
            {matches.map((match, index) => (
                <li key={index}>{matchText(match)}</li>
            ))}
            {phrase && matches.length === 0 && rules.length > 0 && (
                <li>Did not match the search</li>
            )}
        </ul>
    );
};

export const ProductCard = ({ product, href, onOpen, explain }: ProductCardProps) => {
    const { locale } = useStoreInfo();
    return (
        <li className={explain?.hit.explanation ? 'card is-explained' : 'card'}>
            <a
                className="card-link"
                href={href}
                data-sku={product.sku}
                onClick={(event) => {
                    if (!isPlainClick(event)) return;
                    event.preventDefault();
                    onOpen();
                }}
            >
                <Swatch color={product.colors[0]} brand={product.brand.name} size={64} />
                <span className="card-text">
                    <span className="card-name">{product.name}</span>
                    <span className="card-meta">{product.brand.name}</span>
                    <span className="card-price">
                        {formatPriceRange(product.price.min, product.price.max, locale)}
                    </span>
                    {product.price.memberPrice && <span className="card-member">Member price</span>}
                    {/* Every card has a rating line, so one with none reads as none rather than as missing. */}
                    {product.rating && product.rating.count > 0 ? (
                        <span className="card-rating">
                            {/* What is seen, hidden from a screen reader in one place. */}
                            <span className="card-rating-seen" aria-hidden="true">
                                <Stars average={product.rating.average} />
                                <span className="card-rating-average">
                                    {formatAverage(product.rating.average, locale)}
                                </span>
                                <span>({formatCount(product.rating.count, locale)})</span>
                            </span>
                            {/* The rating in words, said once in place of the stars and the figures. */}
                            <span className="visually-hidden">
                                {ratingText(product.rating.average, product.rating.count, locale)}
                            </span>
                        </span>
                    ) : (
                        <span className="card-rating">No ratings yet</span>
                    )}
                    {!product.inStock && <span className="card-stock">Out of stock</span>}
                </span>
            </a>
            {explain && <Explanation {...explain} />}
        </li>
    );
};
